import { useEffect, useMemo } from 'react';
import { createStore, createStateHook, createActionsHook } from 'react-sweet-state';

export type NthUserOrientationState = {
	active: boolean;
	step: 0 | 1 | 2;
	renderedSpotlights: Set<Spotlight>;
	hasInteracted: boolean;
};

export type Tour = keyof typeof TOURS;
export type Spotlight = (typeof TOURS)[Tour][number];

export const TOURS = {
	home: [
		'nth-user-orientation-home-for-you-spotlight',
		'nth-user-orientation-home-space-spotlight',
		'nth-user-orientation-home-template-spotlight',
	] as const,
	'home-fallback': [
		'nth-user-orientation-home-for-you-spotlight',
		'nth-user-orientation-home-space-spotlight',
		'nth-user-orientation-home-create-spotlight',
	] as const,
	editor: [
		'nth-user-orientation-editor-space-spotlight',
		'nth-user-orientation-editor-for-you-spotlight',
		'nth-user-orientation-editor-bpqa-spotlight',
	] as const,
} as const;

const detectTour = (renderedSpotlights: Set<Spotlight>): Tour | null => {
	// Find the first tour where all required spotlights are registered
	for (const [tour, spotlights] of Object.entries(TOURS)) {
		if (spotlights.every((spotlight) => renderedSpotlights.has(spotlight))) {
			return tour as Tour;
		}
	}
	return null;
};

const Store = createStore({
	initialState: {
		active: false,
		step: 0,
		renderedSpotlights: new Set<Spotlight>(),
		hasInteracted: false,
	} as NthUserOrientationState,
	actions: {
		activate:
			() =>
			({ setState }) => {
				setState({ active: true, step: 0 });
			},
		reset:
			() =>
			({ setState }) => {
				setState({ active: false, step: 0, hasInteracted: false });
			},
		next:
			() =>
			({ setState, getState }) => {
				const nextStep = Math.min(getState().step + 1, 2) as 0 | 1 | 2;
				setState({ step: nextStep, hasInteracted: true });
			},
		back:
			() =>
			({ setState, getState }) => {
				const prevStep = Math.max(getState().step - 1, 0) as 0 | 1 | 2;
				setState({ step: prevStep, hasInteracted: true });
			},
		registerSpotlight:
			({ spotlightId }: { spotlightId: Spotlight }) =>
			({ setState, getState }) => {
				const newRegistered = new Set(getState().renderedSpotlights);
				newRegistered.add(spotlightId);
				setState({ renderedSpotlights: newRegistered });
			},
		deregisterSpotlight:
			({ spotlightId }: { spotlightId: Spotlight }) =>
			({ setState, getState }) => {
				const newRegistered = new Set(getState().renderedSpotlights);
				newRegistered.delete(spotlightId);
				setState({ renderedSpotlights: newRegistered });
			},
	},
	name: 'experiment-nth-user-orientation-store',
});

export const useNthUserOrientationState = createStateHook(Store);
export const useNthUserOrientationActions = createActionsHook(Store);

export interface UseTourReturn {
	reset: () => void;
	back: () => void;
	next: () => void;
	currentStep: Spotlight | null;
	progress: { step: number; steps: number };
	hasInteracted: boolean;
}

export const useTour = (spotlightId: Spotlight): UseTourReturn => {
	const { next, back, reset, registerSpotlight, deregisterSpotlight } =
		useNthUserOrientationActions();
	const { step, active, renderedSpotlights, hasInteracted } = useNthUserOrientationState();

	// Auto-register/deregister the spotlight when spotlights use this hook. This way
	// we can keep track of which spotlights are currently rendered, and use that list
	// to figure out if the user is eligible for a tour.
	useEffect(() => {
		registerSpotlight({ spotlightId });
		return () => {
			deregisterSpotlight({ spotlightId });
		};
	}, [spotlightId, registerSpotlight, deregisterSpotlight]);

	const detectedTour = useMemo(() => detectTour(renderedSpotlights), [renderedSpotlights]);

	if (!active || !detectedTour) {
		return {
			reset,
			back,
			next,
			currentStep: null,
			progress: { step: 0, steps: 0 },
			hasInteracted,
		};
	}

	return {
		reset,
		back,
		next,
		currentStep: TOURS[detectedTour][step],
		progress: { step: step + 1, steps: detectedTour ? TOURS[detectedTour].length : 0 },
		hasInteracted,
	};
};
